import { PageGroup } from '@alucio/aws-beacon-amplify/src/models';
import { PageGroupORM } from '../types/orms';

interface GroupDraftLike {
  pages: { pageId: string; id?: string }[];
  name?: string;
}

type DraftPageGroup = Omit<PageGroup, 'id'>;

export type GroupType = PageGroup | PageGroupORM | DraftPageGroup | GroupDraftLike;

export enum GroupSortStrategy {
  ALPHABETICAL = 'ALPHABETICAL',
  BY_FIRST_SLIDE = 'BY_FIRST_SLIDE',
}

export const getPageNumberFromPageId = (pageId: string): number => {
  return parseInt(pageId.split('_').pop() || '0', 10) || 0
}

// Helper to get the first page ID from a group, handling different group structures
const getFirstPageId = (group: GroupType): string | undefined => {
  // Case: PageGroup model (has pageIds array of strings)
  if ('pageIds' in group && group.pageIds && group.pageIds.length) {
    return group.pageIds[0];
  }

  // Case: GroupDraft (has pages array of objects with id)
  if ('pages' in group && group.pages && group.pages.length) {
    return group.pages[0].pageId || group.pages[0].id;
  }

  // Case: PageGroupORM (has model property which is PageGroup)
  if ('model' in group && group.model?.pageIds && group.model.pageIds.length) {
    return group.model.pageIds[0];
  }

  return undefined;
}

const getGroupName = (group: GroupType): string | undefined => {
  if ('name' in group && group.name) return group.name;
  if ('model' in group && group.model && group.model.name) return group.model.name;
  return undefined;
}

/**
 * Sorts groups by the page number of their first slide.
 * If page numbers are equal (or both missing), sorts alphabetically by name.
 * Groups with no slides are placed at the bottom.
 */
const sortGroupsByFirstSlide = (groupA: GroupType, groupB: GroupType) => {
  const firstPageIdA = getFirstPageId(groupA);
  const firstPageIdB = getFirstPageId(groupB);

  if (!firstPageIdA && !firstPageIdB) {
    const nameA = getGroupName(groupA) || '';
    const nameB = getGroupName(groupB) || '';
    return nameA.localeCompare(nameB, 'en', { numeric: true });
  }

  if (!firstPageIdA) return 1;
  if (!firstPageIdB) return -1;

  const pageNumA = getPageNumberFromPageId(firstPageIdA);
  const pageNumB = getPageNumberFromPageId(firstPageIdB);

  if (pageNumA === pageNumB) {
    const nameA = getGroupName(groupA) || '';
    const nameB = getGroupName(groupB) || '';
    return nameA.localeCompare(nameB, 'en', { numeric: true });
  }

  return pageNumA - pageNumB;
}

const sortGroupsAlphabetically = (groupA: GroupType, groupB: GroupType) => {
  const nameA = getGroupName(groupA) || '';
  const nameB = getGroupName(groupB) || '';
  return nameA.localeCompare(nameB, 'en', { numeric: true });
}

export const sortGroups = <T extends GroupType>(
  strategy: GroupSortStrategy = GroupSortStrategy.BY_FIRST_SLIDE,
): (a: T, b: T) => number => {
  const sortFns = {
    [GroupSortStrategy.ALPHABETICAL]: sortGroupsAlphabetically,
    [GroupSortStrategy.BY_FIRST_SLIDE]: sortGroupsByFirstSlide,
  };

  return sortFns[strategy] as (a: T, b: T) => number;
}
